import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { ROUTE } from '@/routes/constants'
import { getSalt } from '@/services/generatedAPIs'
import { useAuth } from '@/contexts/AuthContext'
import { UserInfoResponseRole } from '@/services/generatedAPIs.schemas'
import { generateRequestSignature, hashPassword } from '@/lib/crypto'
import { unlockGamificationAudio } from '@/components/gamification/gamificationAudio'
import { useAsyncAction } from '@/hooks/use-async-action'

// 登录页表单逻辑（接口调用、token 校验、表单校验状态）——抽成单一 Hook，
// 由 PC 端 <LoginForm> 与移动端 <MobileLoginForm> 共用，保证两套布局行为完全一致。
// 接口/auth 流程沿用 github/dev 的 login&logout 重构：login() 内部完成
// setUser / 存 token / 跨标签页广播，登录成功后由「监听 user 的 useEffect」统一跳转；
// 登录错误码分流（80001009 凭据错误、80007000 访问受限跳转）。
export function useLogin() {
  const { t } = useTranslation();
  const [loginMessage, setLogginMessage] = useState<string>("");
  const { tokenLogin, login, user } = useAuth();
  const navigate = useNavigate();
  const { isLoading, execute } = useAsyncAction({ showErrorToast: false });
  const [isEmailInvalid, setEmailInvalid] = useState(false);
  const [isPswInvalid, setPswInvalid] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    // user 已就绪（本 tab 登录成功 / 其他 tab 广播同步）→ 按角色跳转
    if (user) {
      const targetRoute = user.role === UserInfoResponseRole.TEACHER
        ? ROUTE.TEACHER.OVERVIEW
        : ROUTE.STUDENT.OVERVIEW;
      navigate(targetRoute, { replace: true });
      return;
    }
    // 无 user 但有 token → 尝试自动登录，失败则清 token（跳转交给 user 变化后的下一次 effect）
    const token = localStorage.getItem('token');
    if (!token) return;
    let cancelled = false;
    tokenLogin().then((loginUser) => {
      if (cancelled) return;
      if (!loginUser) {
        localStorage.removeItem('token');
      }
    });
    return () => { cancelled = true; };
  }, [navigate, tokenLogin, user])

  const onLogin = (formData: FormData) => execute(async () => {
    void unlockGamificationAudio();
    const email = formData.get("email") as string;
    const password = formData.get("password") as string;
    try {
      const getSaltRsp = await getSalt({ email: email });
      if (getSaltRsp.code === 200 && getSaltRsp.data) {
        const result = await login(
          { email: email, password: hashPassword(password + getSaltRsp.data) },
          { headers: { 'X-Request-Signature': generateRequestSignature() } }
        );
        if (result.success) {
          setLogginMessage(t("user.login-success"));
          // 跳转交给监听 user 的 useEffect 处理
        } else if (result.code === 80001009) {
          setLogginMessage(t("user.login-error"));
        } else if (result.code === 80007000) {
          navigate(ROUTE.ACCESS_RESTRICTED);
        } else {
          setLogginMessage(t("common.network-error"));
        }
      } else {
        setLogginMessage(t("common.network-error"));
      }
    } catch {
      setLogginMessage(t("common.network-error"));
    }
  });

  return {
    t,
    loginMessage,
    isLoading,
    isEmailInvalid,
    isPswInvalid,
    showPassword,
    setShowPassword,
    setEmailInvalid,
    setPswInvalid,
    onLogin,
  };
}
